import { ContentProps, EditorialFactoryProps } from "components/flexComponents/Editorial/typings";
import * as React from "react";
import EditorialBase from "components/flexComponents/Editorial/factory/EditorialBase";
import { EditorialModel, Media } from "typings/microserviceModels/content-flex-module";
import { RenderableContentItem } from "src/components/flexComponents/Editorial/factory/impl/items/RenderableContentItem";
import EditorialItemModel from "components/flexComponents/Editorial/factory/impl/items/EditorialItemModel";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";

export interface DefaultEditorialProps {
  key?: string;
  id?: string;
  text?: string;
  title?: string;
  subtitle?: string;
  view?: string;
  toggle?: string;
  media?: Media;
  padding?: string;
  boxType?: string;
  context?: ExtendedContextStore;
  imageAspectRatio?: string;
  contentSource?: string;
  enableContentSource?: boolean;
  flexViewModel?: FlexViewModelStore;
  index?: number;
  trackRfrridClick?: (
    track: (rfrr: string, linkName: string, delay?: boolean) => void
  ) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export const RenderableContentItemContext = React.createContext<Partial<DefaultEditorialProps>>({});

export const useRenderableContext = () => {
  const renderableContext = React.useContext(RenderableContentItemContext);
  if (!renderableContext) throw new Error("RenderableContentItemContext not found");
  return renderableContext;
};

export class DefaultEditorial extends EditorialBase {
  public commonProps: DefaultEditorialProps;

  /* istanbul ignore next */
  public constructor(editorial: EditorialFactoryProps) {
    super(editorial);
  }

  /**
   * An Editorial consists of HTML strings that we parse through with the best of
   * our abilities and display as is on the page. Mainly, we do not control the
   * content of an Editorial, we simply put them in the page.
   */
  public generate(props: ContentProps) {
    const queryParams = props.routeStore?.queryParams || {};
    const expandEditorialWithId = queryParams.expandEditorial === this.id;
    const {
      title: moduleTitle,
      subtitle: moduleSubtitle,
      padding,
      boxType,
      items,
      enableContentSource,
      toggle: defaultToggle,
      view,
      imageAspectRatio,
    } = this.editorial;
    const contentSource = items[0]?.builtFrom;
    const toggle = expandEditorialWithId ? "expanded" : defaultToggle;
    const classNameAttr = `${this.getEditorialItemClassName()}`;

    return (
      <div className={classNameAttr} id={this.id} data-fm={this.fmId} data-fm-title-id={this.fmTitleId}>
        <div className="EditorialModuleTitles">
          {moduleTitle && (
            <UitkSpacing margin={{ block: "one" }}>
              <UitkHeading tag="h2" size={5}>
                {moduleTitle}
              </UitkHeading>
            </UitkSpacing>
          )}
          {moduleSubtitle && (
            <UitkSpacing margin={{ block: "two" }}>
              <UitkSubheading tag="h3">{moduleSubtitle}</UitkSubheading>
            </UitkSpacing>
          )}
        </div>
        <div>
          {this.editorial.items
            .filter((item: EditorialModel) => item.text || item.title)
            .map((item: EditorialModel, index) => {
              const imageToDisplay = item.media?.find((media) => media.mediaType === "Image" && !!media.mediaUrl);

              return this.getItem(
                new EditorialItemModel(
                  item.id,
                  item.text,
                  item.title,
                  item.subtitle,
                  this.editorial.uitkIconName,
                  view,
                  toggle,
                  this.editorial.floatImage,
                  padding,
                  boxType,
                  imageToDisplay,
                  props.context,
                  imageAspectRatio,
                  contentSource,
                  enableContentSource,
                  index,
                  this.trackRfrridClick
                )
              );
            })}
        </div>
      </div>
    );
  }

  public generateCommonProps = (model: EditorialItemModel) => ({
    id: model.id,
    text: model.text,
    title: model.title,
    subtitle: model.subtitle,
    toggle: model.toggle,
    view: model.view || "",
    key: `${this.contentPurpose}-${this.keyHelper.next()}`,
    padding: model.padding,
    boxType: model.boxType,
    media: model.media,
    context: model.context,
    imageAspectRatio: model.imageAspectRatio,
    contentSource: model.contentSource,
    enableContentSource: model.enableContentSource,
    index: model.index,
    trackRfrridClick: model.trackRfrridClick,
  });

  public getItem(model: EditorialItemModel): JSX.Element {
    this.commonProps = this.generateCommonProps(model);

    return (
      <RenderableContentItemContext.Provider value={this.commonProps}>
        <RenderableContentItem />
      </RenderableContentItemContext.Provider>
    );
  }
}

export default DefaultEditorial;
